import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import { Button, ErrorState, InputText, Spinner } from '../../components'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles, numberText, parseNonNegative } from '../admin/AdminPage'

type SettingsView = RouterOutputs['configuration']['mepSettings']['get']
type Settings = SettingsView['settings']
type SettingsPatch = Partial<Settings>

/** The keys edited as one number. */
type NumberKey =
  | 'assemblyDepartmentId' | 'marginMin' | 'graceMin' | 'startWindowMin' | 'batchWindowMin' | 'batchMaxSize'
  | 'batchMarginalShare' | 'unlockBonusMin' | 'unlockBonusCapMin' | 'historyDays' | 'historyMinSamples'
  | 'recoveryWindowMin' | 'jointEfficiency' | 'conflictToleranceMin' | 'planChangeNoticeMin' | 'pastDays' | 'futureWorkdays'

/** The keys edited as a list of numbers, comma-separated. */
type ListKey =
  | 'departmentIds' | 'outsideDepartmentIds' | 'fallbackConsumerDepartmentIds' | 'ovenTypeIds' | 'mountTypeIds' | 'sharedStationTypeIds'
  | 'heatThresholdsMin' | 'exclamationThresholdsMin'

export type NumberField = {
  key: NumberKey
  label: string
  hint: string
  integer: boolean
  min?: number
  max?: number
}

export type ListField = {
  key: ListKey
  label: string
  hint: string
  /** A fixed count of values (the thresholds); any count otherwise. */
  length?: number
  /** Thresholds can be negative; ids and types cannot. */
  signed?: boolean
}

export type SettingsSection = { legend: string; numbers: Array<NumberField>; lists: Array<ListField> }

/**
 * What stays on Parámetros, one fieldset per part of the plan (§6.2), each key
 * with what it is for. The machines that are not shared and the margin and the
 * heat have screens of their own (MEP-P C).
 */
const PARAMETER_SECTIONS: Array<SettingsSection> = [
  {
    legend: 'Qué es mise en place',
    numbers: [{ key: 'assemblyDepartmentId', label: 'Departamento de ensamblado', hint: 'Sus pasos son lo que la mise en place alimenta (3).', integer: true, min: 1 }],
    lists: [
      { key: 'departmentIds', label: 'Departamentos de mise en place', hint: 'Ids de MRPeasy, separados por comas (1).' },
      { key: 'outsideDepartmentIds', label: 'Departamentos que se cuentan al pie', hint: 'Se cuentan y no se pintan (11).' },
      { key: 'fallbackConsumerDepartmentIds', label: 'Consumidores de reserva', hint: 'Si no hay paso de ensamblado después: el racionado (5).' },
      { key: 'ovenTypeIds', label: 'Tipos de horno', hint: 'Lo que en ensamblado solo pasa por ellos no es base (2, 21, 39, 79).' },
      { key: 'mountTypeIds', label: 'Tipos de montaje', hint: 'Un paso del plato en ellos hace base lo que usa (14, 58).' },
    ],
  },
  {
    legend: 'Días de la bolsa',
    numbers: [
      { key: 'pastDays', label: 'Días atrás de la bolsa', hint: 'Días naturales (7).', integer: true },
      { key: 'futureWorkdays', label: 'Laborables adelante de la bolsa', hint: '(2)', integer: true },
    ],
    lists: [],
  },
  {
    legend: 'Duraciones y lotes',
    numbers: [
      { key: 'historyDays', label: 'Días de histórico', hint: 'Lo marcado en MRPeasy de los últimos N días (28).', integer: true, min: 1 },
      { key: 'historyMinSamples', label: 'Muestras mínimas', hint: 'Por debajo, se usa el tipo de estación o lo planificado (5).', integer: true, min: 1 },
      { key: 'batchWindowMin', label: 'Ventana para juntar un lote (min)', hint: 'Necesidades a como mucho N minutos (60).', integer: true },
      { key: 'batchMaxSize', label: 'Tamaño máximo de un lote', hint: '(6)', integer: true, min: 2 },
      { key: 'batchMarginalShare', label: 'Coste de cada tarea más en un lote', hint: 'De 0 a 1: la más larga más esta parte del resto (0,5).', integer: false, max: 1 },
      { key: 'unlockBonusMin', label: 'Adelanto por paso que desbloquea (min)', hint: '(10)', integer: true },
      { key: 'unlockBonusCapMin', label: 'Adelanto máximo (min)', hint: '(30)', integer: true },
    ],
    lists: [],
  },
  {
    legend: 'Personas',
    numbers: [
      { key: 'recoveryWindowMin', label: 'Tiempo para recuperar lo que va tarde (min)', hint: 'Lo que ya va tarde se reparte en estos minutos (30).', integer: true },
      { key: 'jointEfficiency', label: 'Eficacia de una tarea conjunta', hint: 'De 0 a 1: 1, dos personas tardan la mitad; 0,5, dos tercios; 0, lo mismo que una.', integer: false, max: 1 },
      { key: 'planChangeNoticeMin', label: 'Cambio del plan que se avisa (min)', hint: '(5)', integer: true },
    ],
    lists: [],
  },
]

const SHIFT_IDS = ['manana', 'tarde'] as const

type Draft = {
  numbers: Partial<Record<NumberKey, string>>
  lists: Partial<Record<ListKey, string>>
  people: Record<(typeof SHIFT_IDS)[number], string>
  shifts: Settings['shifts']
  stationGroupIds: Array<string>
}

/**
 * What one screen edits: its fieldsets and, on Parámetros alone, the board's
 * groups, the shifts and the default people.
 */
type ScreenFields = { sections: Array<SettingsSection>; general: boolean }

const numbersOf = (sections: Array<SettingsSection>): Array<NumberField> => sections.flatMap((section) => section.numbers)
const listsOf = (sections: Array<SettingsSection>): Array<ListField> => sections.flatMap((section) => section.lists)

const draftOf = (settings: Settings, sections: Array<SettingsSection>): Draft => ({
  numbers: Object.fromEntries(numbersOf(sections).map((field) => [field.key, numberText(settings[field.key])])),
  lists: Object.fromEntries(listsOf(sections).map((field) => [field.key, settings[field.key].map((value) => numberText(value)).join('; ')])),
  people: { manana: String(settings.defaultPeoplePerShift.manana), tarde: String(settings.defaultPeoplePerShift.tarde) },
  shifts: settings.shifts,
  stationGroupIds: settings.stationGroupIds,
})

const parseNumber = (text: string, field: NumberField): number | null => {
  const parsed = parseNonNegative(text, { integer: field.integer })
  if (!parsed.valid || parsed.value === null) {
    return null
  }
  const { value } = parsed
  return value < (field.min ?? 0) || value > (field.max ?? Number.POSITIVE_INFINITY) ? null : value
}

const SIGNED = /^-?\d+$/

// Lists are separated by `;` (or a comma between integers): a decimal comma never splits a value.
const parseList = (text: string, field: ListField): Array<number> | null => {
  const parts = text.split(/[;,]/).map((part) => part.trim()).filter((part) => part !== '')
  const values = parts.map((part) => (field.signed === true && SIGNED.test(part) ? Number(part) : parseNonNegative(part, { integer: true })))
    .map((parsed) => (typeof parsed === 'number' ? parsed : parsed.valid ? parsed.value : null))
  if (values.some((value) => value === null) || (field.length !== undefined && values.length !== field.length)) {
    return null
  }
  return values.filter((value): value is number => value !== null)
}

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/

type Parsed = { patch: SettingsPatch; invalid: Array<string> }

const setList = (patch: SettingsPatch, key: ListKey, values: Array<number>): void => {
  if (key === 'heatThresholdsMin') {
    patch.heatThresholdsMin = [values[0], values[1], values[2], values[3]]
  } else if (key === 'exclamationThresholdsMin') {
    patch.exclamationThresholdsMin = [values[0], values[1]]
  } else {
    patch[key] = values
  }
}

// The shifts, the default people and the board's groups: the part of the patch only Parámetros edits.
const addGeneral = (settings: Settings, draft: Draft, { patch, invalid }: Parsed): void => {
  const morning = parseNumber(draft.people.manana, { key: 'pastDays', label: '', hint: '', integer: true })
  const afternoon = parseNumber(draft.people.tarde, { key: 'pastDays', label: '', hint: '', integer: true })
  if (morning === null || afternoon === null) {
    invalid.push('Personas por defecto')
  } else if (morning !== settings.defaultPeoplePerShift.manana || afternoon !== settings.defaultPeoplePerShift.tarde) {
    patch.defaultPeoplePerShift = { manana: morning, tarde: afternoon }
  }
  if (draft.shifts.some((shift) => shift.name.trim() === '' || !CLOCK.test(shift.start) || !CLOCK.test(shift.end)
    || shift.start >= shift.end || shift.netMinutes <= 0)) {
    invalid.push('Turnos')
  } else if (JSON.stringify(draft.shifts) !== JSON.stringify(settings.shifts)) {
    patch.shifts = draft.shifts
  }
  if (JSON.stringify(draft.stationGroupIds) !== JSON.stringify(settings.stationGroupIds)) {
    patch.stationGroupIds = draft.stationGroupIds
  }
}

/**
 * The patch the draft makes: the fields of this screen that changed, and the
 * labels of those that do not parse. Nothing of another screen travels, so two
 * screens saved one after the other never undo each other: the API merges the
 * keys given over what is stored.
 */
const patchOf = (settings: Settings, draft: Draft, fields: ScreenFields): Parsed => {
  const parsed: Parsed = { patch: {}, invalid: [] }
  const { patch, invalid } = parsed
  for (const field of numbersOf(fields.sections)) {
    const value = parseNumber(draft.numbers[field.key] ?? '', field)
    if (value === null) {
      invalid.push(field.label)
    } else if (value !== settings[field.key]) {
      patch[field.key] = value
    }
  }
  for (const field of listsOf(fields.sections)) {
    const values = parseList(draft.lists[field.key] ?? '', field)
    if (values === null) {
      invalid.push(field.label)
    } else if (JSON.stringify(values) !== JSON.stringify(settings[field.key])) {
      setList(patch, field.key, values)
    }
  }
  if (fields.general) {
    addGeneral(settings, draft, parsed)
  }
  return parsed
}

type SettingsFormProps = {
  view: SettingsView
  fields: ScreenFields
  saveLabel: string
  savedText: string
}

/**
 * The form, seeded once from the loaded settings. Only what changed travels;
 * a field that does not parse disables the save and is named, so nobody saves
 * a half-read value.
 */
const SettingsForm = ({ view, fields, saveLabel, savedText }: SettingsFormProps): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const { settings } = view
  const [draft, setDraft] = useState<Draft>(() => draftOf(settings, fields.sections))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const update = trpc.configuration.mepSettings.update.useMutation({
    onSuccess: () => {
      setSaved(true)
      void utils.configuration.mepSettings.get.invalidate()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const { patch, invalid } = patchOf(settings, draft, fields)
  const changed = Object.keys(patch).length > 0
  const canSave = !isReadOnly && changed && invalid.length === 0 && !update.isPending

  const edit = (next: Draft): void => {
    setDraft(next)
    setSaved(false)
    setErrorMessage(null)
  }

  const toggleGroup = (groupId: string): void =>
    edit({
      ...draft,
      stationGroupIds: draft.stationGroupIds.includes(groupId)
        ? draft.stationGroupIds.filter((id) => id !== groupId)
        : [...draft.stationGroupIds, groupId],
    })

  /** The marked group at `index` swapped with its neighbour: the board's columns follow this order. */
  const moveGroup = (index: number, neighbour: number): void => {
    const ids = draft.stationGroupIds
    edit({ ...draft, stationGroupIds: ids.map((id, position) => (position === index ? ids[neighbour] : position === neighbour ? ids[index] : id)) })
  }

  const editShift = (index: number, change: Partial<Settings['shifts'][number]>): void =>
    edit({ ...draft, shifts: draft.shifts.map((shift, position) => (position === index ? { ...shift, ...change } : shift)) })

  const nameOf = new Map(view.stationGroups.map((group) => [group.id, group.name]))
  const ordered = [
    ...draft.stationGroupIds.map((id) => ({ id, name: nameOf.get(id) ?? 'Grupo desconocido' })),
    ...view.stationGroups.filter((group) => !draft.stationGroupIds.includes(group.id)),
  ]

  return (
    <form
      className={styles['admin-page__form']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave) {
          update.mutate(patch)
        }
      }}
    >
      {isReadOnly ? <ReadOnlyNote /> : null}

      {fields.general ? (
        <>
          <fieldset className={styles['admin-page__fieldset']}>
            <legend className={styles['admin-page__legend']}>Grupos del tablero</legend>
            <p className={styles['admin-page__note']}>Los grupos de estación que pinta el tablero, en este orden: «Subir» y «Bajar» lo cambian. Sus tipos se asignan en «Grupos de estación».</p>
            <ul className={styles['admin-page__checklist']}>
              {ordered.map((group) => {
                const index = draft.stationGroupIds.indexOf(group.id)
                return (
                  <li key={group.id} className={styles['admin-page__check-option']}>
                    <label className={styles['admin-page__check-text']}>
                      <input
                        type="checkbox"
                        className={styles['admin-page__checkbox']}
                        checked={index >= 0}
                        disabled={isReadOnly}
                        onChange={() => toggleGroup(group.id)}
                      />
                      {group.name}
                    </label>
                    {index >= 0 ? (
                      <div className={styles['admin-page__row-actions']}>
                        <Button
                          variant="secondary"
                          disabled={isReadOnly || index === 0}
                          onClick={() => moveGroup(index, index - 1)}
                          aria-label={`Subir ${group.name}`}
                          label="Subir"
                        />
                        <Button
                          variant="secondary"
                          disabled={isReadOnly || index === draft.stationGroupIds.length - 1}
                          onClick={() => moveGroup(index, index + 1)}
                          aria-label={`Bajar ${group.name}`}
                          label="Bajar"
                        />
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </fieldset>

          <fieldset className={styles['admin-page__fieldset']}>
            <legend className={styles['admin-page__legend']}>Turnos y personas por defecto</legend>
            {draft.shifts.map((shift, index) => (
              <div key={shift.id} className={styles['admin-page__inline-form']}>
                <InputText label={`Nombre del turno ${index + 1}`} value={shift.name} disabled={isReadOnly} onChange={(event) => editShift(index, { name: event.target.value })} />
                <InputText
                  label={`Empieza (${shift.name})`}
                  value={shift.start}
                  disabled={isReadOnly}
                  hint="HH:MM"
                  onChange={(event) => editShift(index, { start: event.target.value.trim() })}
                />
                <InputText
                  label={`Acaba (${shift.name})`}
                  value={shift.end}
                  disabled={isReadOnly}
                  hint="HH:MM"
                  onChange={(event) => editShift(index, { end: event.target.value.trim() })}
                />
                <InputText
                  label={`Minutos netos (${shift.name})`}
                  inputMode="numeric"
                  value={String(shift.netMinutes)}
                  disabled={isReadOnly}
                  onChange={(event) => editShift(index, { netMinutes: Number(event.target.value.replace(/\D/g, '')) })}
                />
              </div>
            ))}
            <div className={styles['admin-page__inline-form']}>
              {SHIFT_IDS.map((shiftId) => (
                <InputText
                  key={shiftId}
                  label={`Personas por defecto (${shiftId === 'manana' ? 'mañana' : 'tarde'})`}
                  inputMode="numeric"
                  value={draft.people[shiftId]}
                  disabled={isReadOnly}
                  onChange={(event) => edit({ ...draft, people: { ...draft.people, [shiftId]: event.target.value } })}
                />
              ))}
            </div>
          </fieldset>
        </>
      ) : null}

      {fields.sections.map((section) => (
        <fieldset key={section.legend} className={styles['admin-page__fieldset']}>
          <legend className={styles['admin-page__legend']}>{section.legend}</legend>
          {section.numbers.map((field) => (
            <InputText
              key={field.key}
              label={field.label}
              hint={field.hint}
              inputMode="decimal"
              value={draft.numbers[field.key] ?? ''}
              disabled={isReadOnly}
              onChange={(event) => edit({ ...draft, numbers: { ...draft.numbers, [field.key]: event.target.value } })}
            />
          ))}
          {section.lists.map((field) => (
            <InputText
              key={field.key}
              label={field.label}
              hint={`${field.hint} Separados por «;».`}
              value={draft.lists[field.key] ?? ''}
              disabled={isReadOnly}
              onChange={(event) => edit({ ...draft, lists: { ...draft.lists, [field.key]: event.target.value } })}
            />
          ))}
        </fieldset>
      ))}

      {fields.general ? (
        <fieldset className={styles['admin-page__fieldset']}>
          <legend className={styles['admin-page__legend']}>Sin editar aquí</legend>
          <p className={styles['admin-page__muted']}>
            Modelo de duración: planificado × mediana de lo real. Cadenas que no se exigen: {settings.chainBreaks.length === 0 ? 'ninguna' : settings.chainBreaks.length}.
          </p>
        </fieldset>
      ) : null}

      {invalid.length > 0 ? <p role="alert" className={styles['admin-page__error']}>Revisa: {invalid.join(', ')}.</p> : null}
      {errorMessage !== null ? <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p> : null}
      {saved && !changed ? <p role="status" className={styles['admin-page__ok']}>{savedText}</p> : null}
      <div className={styles['admin-page__form-actions']}>
        <Button type="submit" disabled={!canSave} label={update.isPending ? 'Guardando…' : saveLabel} />
      </div>
    </form>
  )
}

export type MepSettingsScreenProps = {
  title: string
  intro: string
  sections: Array<SettingsSection>
  /** The board's groups, the shifts, the default people and what is not edited here: Parámetros only. */
  general?: boolean
  saveLabel: string
  savedText: string
}

/**
 * One screen of the mise en place parameters (`mep_settings`). Every screen
 * reads them whole and saves with the same `mepSettings.update`, which leaves
 * its line in the configuration's history, sending only the keys it shows.
 */
export const MepSettingsScreen = ({ title, intro, sections, general = false, saveLabel, savedText }: MepSettingsScreenProps): ReactElement => {
  const settingsQuery = trpc.configuration.mepSettings.get.useQuery()

  let body: ReactElement
  if (settingsQuery.isPending) {
    body = <Spinner label="Cargando parámetros…" />
  } else if (settingsQuery.isError) {
    body = <ErrorState error={settingsQuery.error} onRetry={() => void settingsQuery.refetch()} />
  } else {
    body = <SettingsForm view={settingsQuery.data} fields={{ sections, general }} saveLabel={saveLabel} savedText={savedText} />
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader title={title} intro={intro} />
      {body}
    </section>
  )
}

/**
 * Administración → Mise en place → Parámetros: the board's knobs without a
 * screen of their own, with their default.
 */
export const MepSettingsPage = (): ReactElement => (
  <MepSettingsScreen
    title="Parámetros de mise en place"
    intro="Lo que el tablero de mise en place lee cada vez que se calcula: entre paréntesis, el valor por defecto. Las máquinas que no se comparten y el margen y el calor tienen su pantalla."
    sections={PARAMETER_SECTIONS}
    general
    saveLabel="Guardar parámetros"
    savedText="Parámetros guardados."
  />
)
